import { EINVOICE_STATUS } from '@/utils/einvoice.enum'
import {
  buildLifecycleUiMeta,
  buildReadinessMeta,
  isPreIssuedStatus,
  normalizeWorkState,
} from '@/view/pages/einvoice/utils/lifecycleState.utils'

function hasMeaningfulValue(value) {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  return true
}

function extractLaneErrorCode(item = {}, feedback = '') {
  const blockingReasons = Array.isArray(item?.blockingReasons)
    ? item.blockingReasons
    : Array.isArray(item?.actionContext?.blockingReasons)
      ? item.actionContext.blockingReasons
      : []

  const explicitCode = blockingReasons.find((reason) =>
    [
      'BILL_TYPE_MISMATCH',
      'BILL_NOT_FOUND',
      'INVALID_BILL_TYPE',
      'BILL_MODE_UNSUPPORTED',
      'EINVOICE_SNAPSHOT_MISSING',
      'EINVOICE_SNAPSHOT_REPAIR_REQUIRED',
    ].includes((reason?.code || reason?.reasonCode || '').toString().trim().toUpperCase()),
  )

  if (explicitCode) {
    return (explicitCode.code || explicitCode.reasonCode || '').toString().trim().toUpperCase()
  }

  if (/BILL_TYPE_MISMATCH/i.test(feedback || '')) {
    return 'BILL_TYPE_MISMATCH'
  }

  if (/BILL_NOT_FOUND/i.test(feedback || '')) {
    return 'BILL_NOT_FOUND'
  }

  if (/BILL_MODE_UNSUPPORTED/i.test(feedback || '')) {
    return 'BILL_MODE_UNSUPPORTED'
  }

  if (/EINVOICE_SNAPSHOT_MISSING/i.test(feedback || '')) {
    return 'EINVOICE_SNAPSHOT_MISSING'
  }

  if (/EINVOICE_SNAPSHOT_REPAIR_REQUIRED/i.test(feedback || '')) {
    return 'EINVOICE_SNAPSHOT_REPAIR_REQUIRED'
  }

  return null
}

export function buildListInvoiceIdentity(item = {}) {
  const invoiceSemantic = item?.fieldSemantics?.invoiceNo
  const patternSemantic = item?.fieldSemantics?.pattern
  const serialSemantic = item?.fieldSemantics?.serial
  const hasInvoiceNo = hasMeaningfulValue(item.invoiceNo)
  const hasPattern = hasMeaningfulValue(item.pattern)
  const hasSerial = hasMeaningfulValue(item.serial)
  const invoicePending =
    invoiceSemantic === 'pending_generation' || (!hasInvoiceNo && isPreIssuedStatus(item.status))
  const secondaryPending =
    patternSemantic === 'pending_generation' ||
    serialSemantic === 'pending_generation' ||
    ((!hasPattern || !hasSerial) && isPreIssuedStatus(item.status))

  return {
    primaryText: hasInvoiceNo ? item.invoiceNo : invoicePending ? 'Chưa sinh' : 'Thiếu dữ liệu',
    primaryClass: hasInvoiceNo
      ? ''
      : invoicePending
        ? 'einvoice-list-field--pending'
        : 'einvoice-list-field--missing',
    secondaryText:
      hasPattern || hasSerial
        ? `${item.pattern || '...'} / ${item.serial || '...'}`
        : secondaryPending
          ? 'Sẽ sinh sau phát hành'
          : 'Thiếu dữ liệu hậu phát hành',
    secondaryClass:
      hasPattern || hasSerial
        ? ''
        : secondaryPending
          ? 'einvoice-list-field--pending'
          : 'einvoice-list-field--missing',
  }
}

export function buildListReadiness(item = {}) {
  const workState = normalizeWorkState(item.workState)
  const readiness = buildReadinessMeta(item)

  if (workState === 'pending') {
    return { text: 'Đang xử lý', variant: 'primary' }
  }

  if (workState === 'cancelled') {
    return { text: 'Đã huỷ', variant: 'dark' }
  }

  if (workState === 'adjusted') {
    return { text: 'Đã điều chỉnh', variant: 'warning' }
  }

  if (workState === 'replaced') {
    return { text: 'Đã thay thế', variant: 'warning' }
  }

  if (
    item.status === EINVOICE_STATUS.ISSUE_FAILED ||
    item.status === EINVOICE_STATUS.TAX_REJECTED
  ) {
    return { text: 'Lỗi phát hành', variant: 'danger' }
  }

  if (item.status >= EINVOICE_STATUS.ISSUED) {
    return { text: 'Đã phát hành', variant: 'success' }
  }

  if (readiness.hasBlockingErrors || readiness.isReady === false) {
    return { text: 'Thiếu dữ liệu', variant: 'warning' }
  }

  if (
    readiness.hasPendingGeneration ||
    (!hasMeaningfulValue(item.invoiceNo) && isPreIssuedStatus(item.status))
  ) {
    return { text: '', variant: 'info' }
  }

  return { text: 'Đủ điều kiện', variant: 'secondary' }
}

export function buildListAmountDisplay(item = {}) {
  const preIssued = isPreIssuedStatus(item.status)
  const readiness = buildReadinessMeta(item)

  if (readiness.hasBlockingErrors && preIssued) {
    return {
      toneClass: 'einvoice-list-amount--warning',
      hint: 'Cần kiểm tra lại draft',
    }
  }

  return {
    toneClass: '',
    hint: '',
  }
}

export function buildListBlockerMeta(item = {}) {
  const lifecycleMeta = buildLifecycleUiMeta(item)
  const readiness = buildReadinessMeta(item)
  const feedback = item?.feedbackContent || ''

  const laneErrorCode = extractLaneErrorCode(item, feedback)
  if (laneErrorCode === 'BILL_TYPE_MISMATCH') {
    return {
      text: 'Sai lane e-invoice của hóa đơn',
      variant: 'danger',
    }
  }

  if (laneErrorCode === 'BILL_NOT_FOUND') {
    return {
      text: 'Không tìm thấy hóa đơn theo lane e-invoice',
      variant: 'warning',
    }
  }

  if (laneErrorCode === 'INVALID_BILL_TYPE') {
    return {
      text: 'Loại hóa đơn không hợp lệ',
      variant: 'danger',
    }
  }

  if (laneErrorCode === 'BILL_MODE_UNSUPPORTED') {
    return {
      text: 'Mode hóa đơn giao hàng chưa hỗ trợ',
      variant: 'danger',
    }
  }

  if (laneErrorCode === 'EINVOICE_SNAPSHOT_MISSING') {
    return {
      text: 'Snapshot HĐĐT chưa sẵn sàng',
      variant: 'danger',
    }
  }

  if (laneErrorCode === 'EINVOICE_SNAPSHOT_REPAIR_REQUIRED') {
    return {
      text: 'Snapshot HĐĐT cần backend repair',
      variant: 'danger',
    }
  }

  if (/unitcode is not configured/i.test(feedback)) {
    return {
      text: 'Thiếu cấu hình unitCode',
      variant: 'danger',
    }
  }

  if (lifecycleMeta.workState === 'pending') {
    return {
      text: 'Tạm khóa thao tác',
      variant: 'info',
    }
  }

  if (lifecycleMeta.workState === 'failed') {
    return {
      text: 'Chỉ cho phép thử lại / tạo nháp mới',
      variant: 'danger',
    }
  }

  if (readiness.blockingErrorCount > 0) {
    return {
      text: `${readiness.blockingErrorCount} blocker`,
      variant: 'warning',
    }
  }

  return null
}
